import { Canvas } from '@react-three/fiber';
import { OrbitControls, Line } from '@react-three/drei';
import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import MinecraftCharacterEquip from './MinecraftCharacterEquip'; // Import added
import ModelingCharacter from './ModelingCharacter';
import { IoIosGrid, IoMdGrid, IoMdArrowForward } from "react-icons/io";
import { TbBoxModel2Off } from "react-icons/tb";

interface CustomGridProps {
  size: number;
  divisions: number;
  color: string;
  gridPositionY: number;
  showArrows: boolean;
  onToggleArrows: () => void;
}

function CustomGrid({ size, divisions, color, gridPositionY, showArrows, onToggleArrows }: CustomGridProps) {
  const points: [number, number, number][] = [];
  const step = size / divisions;

  for (let i = -size / 2; i <= size / 2; i += step) {
    points.push([-size / 2, 0, i], [size / 2, 0, i]);
    points.push([i, 0, -size / 2], [i, 0, size / 2]);
  }

  return (
    <group position={[-1, gridPositionY, 0]}>
      {points.map((point, idx) => (
        idx % 2 === 0 && (
          <Line
            key={idx}
            points={[points[idx], points[idx + 1]]}
            color={color}
            lineWidth={1.3}
          />
        )
      ))}
      {showArrows && (
        <>
          <primitive
            object={new THREE.ArrowHelper(new THREE.Vector3(0, 0, -1), new THREE.Vector3(1, gridPositionY + 3, -3.7), 6, 'red')}
          />
          <primitive
            object={new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), new THREE.Vector3(1, gridPositionY + 3, -3.7), 6, 'green')}
          />
          <primitive
            object={new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(1, gridPositionY + 3, -3.7), 6, 'blue')}
          />
        </>
      )}
    </group>
  );
}

export default function ModelingViewer({ modelUrl, modelCategory }: { modelUrl: string; modelCategory: string }) {
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [showArrows, setShowArrows] = useState<boolean>(true);
  const [gridPositionY, setGridPositionY] = useState<number>(-3);
  const [animations, setAnimations] = useState<string[]>(['Idle']);
  const [selectedAnimation, setSelectedAnimation] = useState<string>('Idle');
  const modelRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (modelRef.current) {
      const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
      const minY = boundingBox.min.y;
      setGridPositionY(minY + 0.1);
    }
  }, [modelRef]);

  return (
    <div className="relative w-full h-[500px]  ">
      {/* Responsive Canvas Container */}
      <div className="lg:w-full lg:h-[500px] lg:aspect-none">
        <Canvas className="w-full h-full">
          <ambientLight intensity={2}/>
          <directionalLight position={[2, 2, 2]}/>
          <OrbitControls enableZoom={true}/>

          {showGrid && (
            <CustomGrid
              size={100}
              divisions={40}
              color="#4a5060"
              gridPositionY={gridPositionY}
              showArrows={showArrows}
              onToggleArrows={() => setShowArrows(prev => !prev)}
            />
          )}

          {/* Model Rendering */}
          {['hat', 'left-hand', 'right-hand'].includes(modelCategory) ? (
            <MinecraftCharacterEquip
              modelUrl={modelUrl}
              modelCategory={modelCategory}
            />
          ) : (
            <ModelingCharacter
              modelUrl={modelUrl}
              modelRef={modelRef}
              animationName={selectedAnimation}
              onAnimationsLoaded={setAnimations}
            />
          )}
        </Canvas>
      </div>

      {/* Toggle Grid Button */}
      <button
        onClick={() => setShowGrid(prev => !prev)}
        className="absolute bottom-4 left-4 p-2 bg-white border border-gray-300 rounded-md shadow-md cursor-pointer"
        aria-label="Toggle Grid"
      >
        {showGrid ? <IoIosGrid size={24}/> : <IoMdGrid size={24}/>}
      </button>

      {/* Toggle Arrows Button */}
      <button
        onClick={() => setShowArrows(prev => !prev)}
        className="absolute bottom-4 left-16 p-2 bg-white border border-gray-300 rounded-md shadow-md cursor-pointer"
        aria-label="Toggle Arrows"
      >
        {showArrows ? <IoMdArrowForward size={24}/> : <TbBoxModel2Off size={24}/>}
      </button>

      {/* Animation Selector */}
      <select
        className="w-[150px] lg:w-[200px]  absolute bottom-4 right-4 bg-gray-900 text-white p-2 rounded-md border border-gray-300 cursor-pointer"
        value={selectedAnimation}
        onChange={(e) => setSelectedAnimation(e.target.value)}
        aria-label="Select Animation"
      >
        {animations.map((anim) => (
          <option key={anim} value={anim}>
            {anim}
          </option>
        ))}
      </select>
    </div>
  );
}
